import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import AdminLayout from '../../layouts/AdminLayout'
import { pricingPlanService } from '@services/api'
import useConfirm from '@hooks/useConfirm'

const AdminPricingPage = () => {
  const { confirm, ConfirmDialog } = useConfirm()
  // Vérification des permissions dès le début
  const getCurrentUserRoles = () => {
    const storedUser = localStorage.getItem('admin_user') || localStorage.getItem('user')
    if (!storedUser) return []
    try {
      const userData = JSON.parse(storedUser)
      const normalize = (r) => (typeof r === 'string' ? r : r?.name ?? '')
      if (Array.isArray(userData.roles)) return userData.roles.map(normalize)
      if (typeof userData.roles === 'string') return userData.roles.split(',').map(r => r.trim())
      if (userData.role) return [normalize(userData.role)]
    } catch (e) {}
    return []
  }

  const userRoles = getCurrentUserRoles()
  const hasAccess = userRoles.length === 0 || userRoles.some(r =>
    ['super admin', 'admin', 'superviseur'].includes(r.toLowerCase())
  )

  const [loading, setLoading] = useState(true)
  const [pricingPlans, setPricingPlans] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [editingPlan, setEditingPlan] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    duration_days: '30',
    features: [],
    description: '',
    is_popular: false,
    is_active: true
  })

  const predefinedFeatures = [
    'Publication standard',
    'Publication en vedette',
    'Vidéo promotionnelle',
    'Bannière publicitaire',
    'Support prioritaire',
    'Statistiques avancées',
    'Badge vérifié',
    'Partage illimité',
    'Promotion sur réseaux sociaux',
    'Rapport mensuel détaillé'
  ]

  // Charger les plans au montage du composant
  useEffect(() => {
    if (hasAccess) {
      loadPricingPlans()
    } else {
      setLoading(false)
    }
  }, [hasAccess])

  const loadPricingPlans = async () => {
    try {
      const result = await pricingPlanService.getPricingPlans()
      setPricingPlans(result.data || [])
    } catch (error) {
      console.error('Erreur chargement tarifs:', error)
      toast.error('Erreur lors du chargement des tarifs')
    } finally {
      setLoading(false)
    }
  }

  // Si pas d'accès, afficher message d'erreur
  if (!hasAccess) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Accès refusé</h2>
            <p className="text-gray-600">Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
            <p className="text-sm text-gray-500 mt-2">Rôles autorisés : Super Admin, Admin, Superviseur</p>
          </div>
        </div>
      </AdminLayout>
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      const payload = {
        ...formData,
        features: JSON.stringify(formData.features)
      }

      if (editingPlan) {
        await pricingPlanService.updatePricingPlan(editingPlan.id, payload)
        toast.success('Plan mis à jour avec succès')
      } else {
        await pricingPlanService.createPricingPlan(payload)
        toast.success('Plan créé avec succès')
      }

      setShowModal(false)
      resetForm()
      loadPricingPlans()
    } catch (error) {
      console.error('Erreur:', error)
      toast.error('Erreur lors de la sauvegarde du plan')
    }
  }

  const handleEdit = (plan) => {
    setEditingPlan(plan)
    setFormData({
      name: plan.name,
      price: plan.price,
      duration_days: plan.duration_days,
      features: Array.isArray(plan.features) ? plan.features : [],
      description: plan.description || '',
      is_popular: Boolean(plan.is_popular),
      is_active: Boolean(plan.is_active)
    })
    setShowModal(true)
  }

  const handleDelete = async (planId) => {
    if (!(await confirm({ title: 'Supprimer le plan', message: 'Êtes-vous sûr de vouloir supprimer ce plan ?', type: 'danger', confirmText: 'Supprimer' }))) return

    try {
      await pricingPlanService.deletePricingPlan(planId)
      toast.success('Plan supprimé avec succès')
      loadPricingPlans()
    } catch (error) {
      toast.error('Erreur lors de la suppression')
    }
  }

  const toggleFeature = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }))
  }

  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      duration_days: '30',
      features: [],
      description: '',
      is_popular: false,
      is_active: true
    })
    setEditingPlan(null)
  }

  return (
    <AdminLayout>
      <div className="space-y-6 mt-10">
        {/* Header */}
        <div className="bg-white rounded-md border border-slate-200 p-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-800">Tarification</h1>
              <p className="text-gray-500 mt-2 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Gestion des plans tarifaires de la plateforme
              </p>
            </div>
            <button
              onClick={() => {
                resetForm()
                setShowModal(true)
              }}
              className="px-6 py-2.5 bg-gradient-to-r from-[#FFD700] to-[#F5C800] text-[#002A4E] rounded-md transition-all duration-200 flex items-center gap-2 font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nouveau Plan
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5B700]"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`bg-white rounded-md border-2 ${
 plan.is_popular ? 'border-[#F5B700]' : 'border-slate-200'
 } p-6 transition-shadow relative border border-slate-200`}
              >
                {plan.is_popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-[#FFD700] to-[#F5C800] text-[#002A4E] px-4 py-1 rounded-full text-xs font-semibold">
                      POPULAIRE
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-[#B8860B]">{plan.price}</span>
                    <span className="text-gray-500">FCFA</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{plan.duration_days} jours</p>
                </div>

                {plan.description && (
                  <p className="text-sm text-gray-600 mb-4 text-center">{plan.description}</p>
                )}

                <div className="space-y-3 mb-6">
                  {(Array.isArray(plan.features) ? plan.features : []).map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    plan.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {plan.is_active ? 'Actif' : 'Inactif'}
                  </span>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(plan)}
                      className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                      title="Modifier"
                    >
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(plan.id)}
                      className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                      title="Supprimer"
                    >
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-md max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-200">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 rounded-t-md">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingPlan ? 'Modifier le plan' : 'Nouveau plan tarifaire'}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-md">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom du plan <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                      placeholder="Ex: Premium"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prix (FCFA) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                      placeholder="10000"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durée (jours) <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.duration_days}
                    onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                  >
                    <option value="7">7 jours</option>
                    <option value="15">15 jours</option>
                    <option value="30">30 jours</option>
                    <option value="60">60 jours</option>
                    <option value="90">90 jours</option>
                    <option value="180">180 jours</option>
                    <option value="365">365 jours</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                    rows={2}
                    placeholder="Description courte du plan"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fonctionnalités</label>
                  <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3">
                    {predefinedFeatures.map((feature) => (
                      <label key={feature} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={formData.features.includes(feature)}
                          onChange={() => toggleFeature(feature)}
                          className="w-4 h-4 text-[#B8860B] rounded focus:ring-amber-200"
                        />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_popular}
                      onChange={(e) => setFormData({ ...formData, is_popular: e.target.checked })}
                      className="w-4 h-4 text-[#B8860B] rounded focus:ring-amber-200"
                    />
                    <span className="text-sm text-gray-700">Plan populaire</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-[#B8860B] rounded focus:ring-amber-200"
                    />
                    <span className="text-sm text-gray-700">Plan actif</span>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-6 py-2.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-2.5 bg-gradient-to-r from-[#FFD700] to-[#F5C800] text-[#002A4E] rounded-md transition-all duration-200 font-medium"
                  >
                    {editingPlan ? 'Mettre à jour' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      {ConfirmDialog}
    </AdminLayout>
  )
}

export default AdminPricingPage
